import React from "react";
import { useQuery } from "@tanstack/react-query";
import { systemApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { useAuth } from "@/store/auth";
import { Mono, StatusDot } from "@/components/Atoms";
import { useCan } from "@/lib/rbac";

export default function System() {
  const canReadPool = useCan("read:pool_stats");
  const health = useQuery({ queryKey: ["health"], queryFn: systemApi.health });
  const pool = useQuery({
    queryKey: ["pool"],
    queryFn: systemApi.pool,
    // Only admin/service are allowed to read pool stats. Skipping the call
    // for operators/viewers avoids a 403 toast on page load.
    enabled: canReadPool,
    retry: 0,
  });
  const username = useAuth((s) => s.username);
  const role = useAuth((s) => s.role);
  const expires_at = useAuth((s) => s.expires_at);

  const expiresLabel = expires_at
    ? new Date(expires_at).toLocaleString()
    : "—";

  return (
    <PageContainer>
      <PageHeader title="System" subtitle="Backend health, connection pool, and your current session." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-zinc-200 rounded-lg p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">/healthz</div>
          <StatusDot online={health.data?.status === "ok"} label={health.data?.status || "…"} />
          <div className="mt-3 text-xs font-mono text-zinc-500">
            uptime <span className="text-zinc-800">{health.data?.uptime_s ?? "—"}s</span> ·
            version <span className="text-zinc-800">{health.data?.version || "—"}</span>
          </div>
        </div>
        {pool.data && Object.entries(pool.data)
          // Drop uptime_s — already shown in the /healthz chip above.
          .filter(([k]) => k !== "uptime_s")
          // Flatten nested objects (e.g. `db: { idle, in_use, ... }`) so each
          // metric gets its own chip instead of stringifying to `[object Object]`.
          .flatMap(([k, v]) =>
            v && typeof v === "object" && !Array.isArray(v)
              ? Object.entries(v).map(([subK, subV]) => [`${k}.${subK}`, subV])
              : [[k, v]]
          )
          .map(([k, v]) => (
            <div key={k} className="bg-white border border-zinc-200 rounded-lg p-5">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{k}</div>
              <div className="font-heading text-2xl font-bold mt-1.5" data-testid={`pool-stat-${k.replace(/\./g,'-')}`}>
                {v == null ? "—" : String(v)}
              </div>
            </div>
          ))}
      </div>

      <div className="mt-6 bg-white border border-zinc-200 rounded-lg p-5 max-w-xl" data-testid="system-session-card">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">current session</div>
        <div className="mt-2 grid grid-cols-2 gap-y-2 text-sm">
          <div className="text-zinc-500">Username</div>
          <div><Mono>{username || "—"}</Mono></div>
          <div className="text-zinc-500">Role</div>
          <div><Mono>{role || "—"}</Mono></div>
          <div className="text-zinc-500">Expires</div>
          <div className="font-mono text-xs">{expiresLabel}</div>
        </div>
        <div className="mt-3 text-xs text-zinc-500">
          Token is stored locally and sent as <Mono>Authorization: Bearer</Mono> on every API call.
        </div>
      </div>
    </PageContainer>
  );
}
